/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import React, { useEffect, useRef, useState, useContext } from 'react';
import { AuthContext } from '../../../Contexts/AuthContext';
import { MODULE_NAMES } from '../../../Constants/modules.js';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import FormDialog from '../../../Components/Modal/modal.js';
import './viewResume.scss';
import { CLOSE_TEXT, STAFF_AUGMENTATION } from '../../../Constants/strings.js';
import ThreeDotsVertical from '../../../Assets/images/svg/three_dots_vertical.svg';
import GenerateResumeDialog from './generateResume.js';
import UseAxios from '../../../Api/http/http.js';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider.js';
import { convertResume } from '../../../Api/request/resourceRequest.js';
import ResumeWatermarkDialog from './resumeWatermarkPopUp.js';
import PuzzleLoader from '../../../Assets/images/svg/loading puzzle.gif';
const ResumeIframe = ({ resumeFileUrl, onLoad }) => {
  if (!resumeFileUrl) return null;

  const encodedUrl = encodeURIComponent(resumeFileUrl);
  const googleDocsViewerUrl = `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;

  return (
    <iframe
      src={googleDocsViewerUrl}
      title="Resume Viewer"
      width="100%"
      height="600px"
      className="resume-iframe-style"
      style={{ position: 'relative', zIndex: 1 }}
      onLoad={onLoad}
    />
  );
};

const ViewResumeDialog = ({
  modalOpen,
  handleClose,
  resumeFileUrl,
  htmlResumeContent,
  selectedResumeRow,
  generateResumeDialogOpen,
  setGenerateResumeDialogOpen,
  handleEditResumeClick,
  setHtmlResumeContent,
  fetchAndSetResumeUrl,
  lastUpdatedBy,
  setViewFileLoader,
  viewFileLoader,
  setRows,
  resumeUpdateProgress,
  setResumeUpdateProgress,
}) => {
  // Permission logic
  const { userProfile } = useContext(AuthContext);
  const userPermissions = userProfile?.permissions || {};
  // Edit Resume
  const canEditResume
    = userPermissions[
      MODULE_NAMES.STAFF_AUG_RESUME_GENERATION_VIEW_RESUME_EDIT_RESUME
    ]?.includes('update');
  // Download Resume
  const canDownloadResume
    = userPermissions[
      MODULE_NAMES.STAFF_AUG_RESUME_GENERATION_VIEW_RESUME_DOWNLOAD_RESUME
    ]?.includes('read');
  // Convert Resume
  const canConvertResume
    = userPermissions[
      MODULE_NAMES.STAFF_AUG_RESUME_GENERATION_VIEW_RESUME_CONVERT_TO_NIVEUS
    ]?.includes('create');
  const [selectedRow, setSelectedRow] = useState(selectedResumeRow);
  const [menuOpen, setMenuOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const menuButtonRef = useRef(null);
  const popupRef = useRef(null);
  const { snackbarOpen } = useSnackbar();
  const content = STAFF_AUGMENTATION.REQUEST_RESOURCING;
  const [resumeWatermarkModalOpen, setResumeWatermarkModalOpen]
    = useState(false);
  const [isWatermark, setIsWatermark] = useState(true);

  useEffect(() => {
    if (resumeFileUrl) {
      setViewFileLoader(true);
    }
  }, [resumeFileUrl, setViewFileLoader]);

  useEffect(() => {
    setSelectedRow(selectedResumeRow);
  }, [selectedResumeRow]);

  const handleEditClick = () => {
    handleEditResumeClick(selectedRow);
    setMenuOpen(false);
  };

  const handleConvert = () => {
    setMenuOpen(false);
    setResumeWatermarkModalOpen(true);
  };

  const handleResumeConversion = async () => {
    setResumeWatermarkModalOpen(false);
    setViewFileLoader(true);
    const payload = {
      staffingId: selectedRow?.staffingId,
      employeeId: selectedRow?.employeeId,
      lastUpdatedBy: lastUpdatedBy,
      watermark: isWatermark,
      others: selectedRow?.others,
      resumeTrackerId: selectedRow?.resumeTrackerId,
    };
    const response = await UseAxios(convertResume(payload));
    if (response?.status === 200) {
      snackbarOpen(
        content.RESUME_CONVERSION_SUCCESS,
        STAFF_AUGMENTATION.SUCCESS_SEVERITY,
      );
      setRows((prevRows) =>
        prevRows.map((row) => {
          const isEmployee
            = row.staffingId === selectedRow.staffingId
            && row.employeeId
            && row.employeeId !== 'N/A'
            && row.employeeId === selectedRow.employeeId;

          const isConsultant
            = row.staffingId === selectedRow.staffingId
            && (!row.employeeId || row.employeeId === 'N/A')
            && row.resumeTrackerId === selectedRow.resumeTrackerId;

          if (isEmployee || isConsultant) {
            const updatedRow = {
              ...row,
              showConvert: false,
              showEdit: true,
            };
            setSelectedRow(updatedRow);
            return updatedRow;
          }
          return row;
        }),
      );
      setTimeout(() => {
        fetchAndSetResumeUrl(
          selectedRow?.employeeId,
          selectedRow?.staffingId,
          selectedRow?.resumeTrackerId,
        );
      }, 500);
    } else {
      snackbarOpen(
        content.RESUME_CONVERSION_FAILED,
        STAFF_AUGMENTATION.ERROR_SEVERITY,
      );
    }
  };

  const handleDownloadClick = () => {
    if (!resumeFileUrl) return;
    const link = document.createElement('a');
    link.href = resumeFileUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setMenuOpen(false);
  };

  const handleMenuClick = () => {
    const rect = menuButtonRef.current.getBoundingClientRect();
    setPopupPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX - 130,
    });
    setMenuOpen(true);
  };

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (
        menuButtonRef.current
        && !menuButtonRef.current.contains(event.target)
        && popupRef.current
        && !popupRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div>
      {!generateResumeDialogOpen && (
        <FormDialog
          title={
            <div className="viewResume-header-row">
              <div>
                <span className="viewResume-header-title">
                  {STAFF_AUGMENTATION.RESUME}
                </span>
              </div>
              <div>
                <button
                  type="button"
                  id="resume-view-menu-button"
                  aria-label="menu"
                  onClick={handleMenuClick}
                  ref={menuButtonRef}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                >
                  <img src={ThreeDotsVertical} alt="Menu" />
                </button>
                {menuOpen
                  && ReactDOM.createPortal(
                    <div
                      ref={popupRef}
                      className="view-resume-menu-card"
                      role="menu"
                      tabIndex={-1}
                      style={{
                        position: 'absolute',
                        top: popupPosition.top,
                        left: popupPosition.left,
                        zIndex: 9999,
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        className="view-resume-menu-option"
                        onClick={
                          canEditResume && selectedRow?.showEdit
                            ? handleEditClick
                            : undefined
                        }
                        role="menuitem"
                        disabled={!canEditResume || !selectedRow?.showEdit}
                      >
                        Edit
                      </button>
                      <button
                        className="view-resume-menu-option"
                        onClick={
                          canDownloadResume ? handleDownloadClick : undefined
                        }
                        role="menuitem"
                        disabled={!canDownloadResume}
                      >
                        Download
                      </button>
                      <button
                        className="view-resume-menu-option"
                        onClick={
                          canConvertResume && selectedRow?.showConvert
                            ? handleConvert
                            : undefined
                        }
                        role="menuitem"
                        disabled={
                          !canConvertResume || !selectedRow?.showConvert
                        }
                      >
                        Convert to Niveus format
                      </button>
                    </div>,
                    document.body,
                  )}
              </div>
            </div>
          }
          open={modalOpen}
          button1Action={handleClose}
          dialogPaperCss="view-resume-dialog-paper"
          dialogCss="view-resume-dialog"
          dialogBodycontent={
            <div className="view-resume-dialog-body">
              <div className="viewResume-content-container">
                {viewFileLoader && (
                  <div className="viewResume-loader">
                    <img
                      src={PuzzleLoader}
                      alt="Loading..."
                      className="loader-gif"
                      style={{
                        position: 'absolute',
                        marginBottom: '150px',
                      }}
                    />
                  </div>
                )}
                <ResumeIframe
                  resumeFileUrl={resumeFileUrl}
                  onLoad={() => setViewFileLoader(false)}
                />
              </div>
            </div>
          }
          button1={CLOSE_TEXT}
          submitButtonId="generic-email-submit-btn"
        />
      )}
      <GenerateResumeDialog
        modalOpen={generateResumeDialogOpen}
        setGenerateResumeDialogOpen={setGenerateResumeDialogOpen}
        isEdit={true}
        htmlResumeContent={htmlResumeContent}
        setHtmlResumeContent={setHtmlResumeContent}
        selectedRow={selectedRow}
        fetchAndSetResumeUrl={fetchAndSetResumeUrl}
        setResumeUpdateProgress={setResumeUpdateProgress}
        resumeUpdateProgress={resumeUpdateProgress}
      />
      <ResumeWatermarkDialog
        openModal={resumeWatermarkModalOpen}
        handleClose={() => {
          setResumeWatermarkModalOpen(false);
        }}
        selectedResumeRow={selectedResumeRow}
        onSubmitResumeGenerate={handleResumeConversion}
        isCheckboxChecked={isWatermark}
        setIsCheckboxChecked={setIsWatermark}
        onCheckboxChange={(checked) => setIsWatermark(checked)}
        generateResume={false}
      />
    </div>
  );
};

ViewResumeDialog.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  resumeFileUrl: PropTypes.string,
  htmlResumeContent: PropTypes.string,
  selectedResumeRow: PropTypes.object,
  generateResumeDialogOpen: PropTypes.bool.isRequired,
  setGenerateResumeDialogOpen: PropTypes.func.isRequired,
  handleEditResumeClick: PropTypes.func.isRequired,
  setHtmlResumeContent: PropTypes.func.isRequired,
  fetchAndSetResumeUrl: PropTypes.func.isRequired,
  lastUpdatedBy: PropTypes.string.isRequired,
  setViewFileLoader: PropTypes.func.isRequired,
  viewFileLoader: PropTypes.bool.isRequired,
  setRows: PropTypes.func.isRequired,
  resumeUpdateProgress: PropTypes.bool.isRequired,
  setResumeUpdateProgress: PropTypes.func.isRequired,
};

ResumeIframe.propTypes = {
  resumeFileUrl: PropTypes.string,
  onLoad: PropTypes.func,
};

export default ViewResumeDialog;
